import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { EnvelopeSimple, Lock, User, HardHat, ArrowRight, Gift, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage({ onSwitchToLogin, initialPromoCode }) {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promoCode, setPromoCode] = useState(initialPromoCode || '');
  const [promoValid, setPromoValid] = useState(null);
  const [promoLabel, setPromoLabel] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const checkPromo = async (code) => {
    if (!code || code.length < 3) { setPromoValid(null); return; }
    try {
      const { data } = await axios.get(`${API}/promo/validate/${code.trim()}`);
      setPromoValid(true);
      setPromoLabel(data.duration_label);
    } catch {
      setPromoValid(false);
      setPromoLabel('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    setLoading(true);
    const result = await register(name, email, password, promoCode || undefined, promoCode || undefined);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1759389964108-1940ff61db0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjcmFmdHNtYW4lMjBidWlsZGluZyUyMHRyb3BpY2FsJTIwaXNsYW5kJTIwaG9tZXxlbnwwfHx8fDE3NzYxMzA5MjR8MA&ixlib=rb-4.1.0&q=85"
          alt="Villa tropicale Mayotte" className="object-cover w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
              <HardHat size={24} weight="duotone" className="text-white" />
            </div>
            <span className="text-white font-bold text-xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Shanga Pro</span>
          </div>
          <p className="text-white/90 text-lg leading-relaxed" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Rejoignez les artisans qui boostent leur visibilité en ligne.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-[#09090B]">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
              <HardHat size={24} weight="duotone" className="text-white" />
            </div>
            <span className="font-bold text-xl text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Shanga Pro</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Créer un compte</h1>
          <p className="text-[#A1A1AA] mb-8">Commencez à générer vos posts marketing</p>
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="register-error">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5 text-[#E4E4E7]">Nom / Entreprise</label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input data-testid="register-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border border-[#27272A] rounded-lg bg-[#18181B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none transition-all placeholder:text-[#71717A]"
                  placeholder="Votre nom ou entreprise" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5 text-[#E4E4E7]">Email</label>
              <div className="relative">
                <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border border-[#27272A] rounded-lg bg-[#18181B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none transition-all placeholder:text-[#71717A]"
                  placeholder="votre@email.com" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5 text-[#E4E4E7]">Mot de passe</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border border-[#27272A] rounded-lg bg-[#18181B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none transition-all placeholder:text-[#71717A]"
                  placeholder="Min. 6 caractères" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5 text-[#E4E4E7]">Code promo / Invitation (optionnel)</label>
              <div className="relative">
                <Gift size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input data-testid="register-promo-input" type="text" value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value); checkPromo(e.target.value); }}
                  className={`w-full h-12 pl-10 pr-10 border rounded-lg bg-[#18181B] text-[#F4F4F5] focus:ring-1 outline-none transition-all placeholder:text-[#71717A] ${promoValid === true ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : promoValid === false ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[#27272A] focus:border-[#8B5CF6] focus:ring-[#8B5CF6]'}`}
                  placeholder="Ex: AMIBTP" />
                {promoValid === true && <CheckCircle size={20} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-green-400" />}
              </div>
              {promoValid === true && <p className="text-xs text-green-400 mt-1">Code valide — Pro gratuit {promoLabel}</p>}
              {promoValid === false && <p className="text-xs text-red-400 mt-1">Code invalide ou expiré</p>}
            </div>
            <button data-testid="register-submit-button" type="submit" disabled={loading}
              className="w-full h-12 bg-[#8B5CF6] text-white font-bold rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? 'Création...' : <>Créer mon compte <ArrowRight size={18} weight="bold" /></>}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-[#A1A1AA]">
            Déjà un compte ?{' '}
            <button data-testid="switch-to-login" onClick={onSwitchToLogin} className="text-[#A78BFA] font-semibold hover:underline">Se connecter</button>
          </p>
        </div>
      </div>
    </div>
  );
}
